import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Inbox, Loader2, MapPin, Send, CheckCheck, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * P4 · Provider Inbox — /transactions/inbox
 * Lists open transaction Intents that match the provider's own listing
 * categories + geo. One-tap "Send Offer" opens a compact price sheet.
 */
export default function InboxPage() {
  const nav = useNavigate();
  const [items, setItems]     = useState([]);
  const [meta, setMeta]       = useState(null);
  const [busy, setBusy]       = useState(true);
  const [radius, setRadius]   = useState(50);
  const [offerFor, setOfferFor] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/transactions/inbox?radius_km=${radius}`);
      setItems(r.data.items || []);
      setMeta(r.data);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Please log in."); nav("/"); return; }
      toast.error("Couldn't load inbox");
    } finally { setBusy(false); }
  }, [nav, radius]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 pb-20" data-testid="txn-inbox">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center gap-2">
          <button onClick={() => nav(-1)} className="p-2 -ml-1 rounded-full hover:bg-stone-100" data-testid="inbox-back">
            <ArrowLeft size={18}/>
          </button>
          <Inbox size={16} className="text-emerald-600"/>
          <div className="font-black text-stone-900">Provider Inbox</div>
          <div className="ml-auto text-[11px] text-stone-500 font-bold" data-testid="inbox-count">
            {items.length}
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-3 pb-2 flex items-center gap-2 text-[11px]">
          <span className="font-bold text-stone-500">Radius</span>
          <input
            type="range" min="5" max="200" step="5"
            value={radius} onChange={(e) => setRadius(Number(e.target.value))}
            className="flex-1"
            data-testid="inbox-radius"
          />
          <span className="font-black text-stone-800 w-14 text-right">{radius} km</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-3 space-y-2">
        {meta && meta.provider_categories && meta.provider_categories.length > 0 && (
          <div className="text-[11px] text-stone-500 font-bold" data-testid="inbox-cats">
            Matching: {meta.provider_categories.join(" · ")}
          </div>
        )}
        {meta && (!meta.provider_categories || meta.provider_categories.length === 0) && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="inbox-no-listings">
            {meta.hint || "Add a listing to unlock the provider inbox."}
          </div>
        )}
        {busy && items.length === 0 && (
          <div className="py-16 text-center text-stone-500">
            <Loader2 size={20} className="animate-spin inline-block mr-1.5"/> Loading…
          </div>
        )}
        {!busy && items.length === 0 && (
          <div className="py-16 text-center text-stone-500 text-sm" data-testid="inbox-empty">
            No matching open Needs right now.
          </div>
        )}
        {items.map((it) => (
          <div key={it.intent_id}
               className="w-full text-left bg-white border border-stone-200 rounded-2xl p-3"
               data-testid={`inbox-row-${it.intent_id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-black text-stone-900 text-sm truncate">{it.product}</div>
                <div className="text-xs text-stone-500 font-bold mt-0.5">
                  {it.quantity} {it.unit || "piece"}
                  {it.target_price != null && ` · target ${it.currency || "INR"} ${Number(it.target_price).toLocaleString("en-IN")}`}
                </div>
                <div className="text-[10px] text-stone-500 mt-1 flex items-center gap-2 flex-wrap">
                  {it.category && <span className="uppercase font-black">{it.category}</span>}
                  {it.distance_km != null && (
                    <span className="inline-flex items-center gap-0.5"><MapPin size={10}/> {it.distance_km} km</span>
                  )}
                  <span className="px-1.5 py-0.5 rounded-full bg-stone-100 font-black uppercase">{it.state}</span>
                  {it.delivery_required && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-black uppercase">Delivery</span>}
                </div>
              </div>
              {it.already_offered ? (
                <div className="text-[10px] font-black text-emerald-700 flex items-center gap-1">
                  <CheckCheck size={12}/> Offered
                </div>
              ) : (
                <button
                  data-testid={`inbox-offer-btn-${it.intent_id}`}
                  onClick={() => setOfferFor(it)}
                  className="shrink-0 px-3 py-2 rounded-full bg-emerald-600 text-white text-[11px] font-black flex items-center gap-1">
                  <Send size={12}/> Send Offer
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <OfferSheet
        intent={offerFor}
        onClose={() => setOfferFor(null)}
        onCreated={() => { setOfferFor(null); load(); }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function OfferSheet({ intent, onClose, onCreated }) {
  const [price, setPrice] = useState("");
  const [qty, setQty]     = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!intent) return;
    setPrice(intent.target_price != null ? String(intent.target_price) : "");
    setQty(intent.quantity != null ? String(intent.quantity) : "");
    setNotes("");
  }, [intent]);

  if (!intent) return null;

  const submit = async () => {
    if (!(Number(price) > 0)) { toast.error("Price required"); return; }
    setSaving(true);
    try {
      await api.post(`/transactions/intents/${intent.intent_id}/offers`, {
        price: Number(price),
        quantity: qty === "" ? null : Number(qty),
        currency: intent.currency || "INR",
        notes,
      });
      toast.success("Offer sent");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send offer");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end" onClick={onClose} data-testid="inbox-offer-sheet">
      <div className="w-full bg-white rounded-t-3xl p-5 mx-auto max-w-2xl space-y-3"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase font-black text-emerald-700">Send Offer</div>
            <h3 className="text-lg font-black text-stone-900 truncate">{intent.product}</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-stone-100 grid place-items-center" data-testid="inbox-offer-close">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] font-bold text-stone-600 uppercase">Price ({intent.currency || "INR"})</label>
            <input data-testid="inbox-offer-price" type="number" min="0" step="any"
                   className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm"
                   value={price} onChange={(e) => setPrice(e.target.value)}/>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-stone-600 uppercase">Quantity</label>
            <input data-testid="inbox-offer-qty" type="number" min="0" step="any"
                   className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm"
                   value={qty} onChange={(e) => setQty(e.target.value)}/>
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-bold text-stone-600 uppercase">Notes</label>
          <textarea data-testid="inbox-offer-notes" rows={2}
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm"
                    value={notes} onChange={(e) => setNotes(e.target.value)}/>
        </div>
        <button
          data-testid="inbox-offer-submit"
          disabled={saving}
          onClick={submit}
          className="w-full mt-1 px-4 py-3 rounded-full bg-emerald-600 text-white font-black text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
          {saving ? "Sending…" : "Send Offer"}
        </button>
      </div>
    </div>
  );
}
